import adminApi, { UsageDailyResponse, UsagePeriodRecord } from '@/services/admin.api';
import BillingTimeframeSelect, {
  timeframeParams,
  useBillingTimeframe,
} from '@/components/billing/BillingTimeframeSelect';
import { formatTokensCompact } from '@/utils/formatTokens.util';
import {
  Api,
  Forum,
  History,
  MonetizationOn,
  SmartToy,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import React, { useEffect, useMemo, useState } from 'react';

type MetricKey = 'messagesUsed' | 'apiCallsUsed' | 'aiTokensUsed' | 'aiCostUsd';

const METRIC_CONFIG: Record<MetricKey, { label: string; format: (n: number) => string }> = {
  messagesUsed: { label: 'Messages', format: (n) => Math.round(n).toLocaleString() },
  apiCallsUsed: { label: 'API Calls', format: (n) => Math.round(n).toLocaleString() },
  aiTokensUsed: { label: 'AI Tokens', format: (n) => formatTokensCompact(n) },
  aiCostUsd: { label: 'AI Cost', format: (n) => `$${n.toFixed(2)}` },
};

function fmtDate(value: string) {
  return new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function fmtDateFull(value: string) {
  return new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

const StatTile: React.FC<{ icon: React.ReactNode; label: string; value: string }> = ({ icon, label, value }) => (
  <Box
    sx={(theme) => ({
      p: 2.5,
      height: '100%',
      borderRadius: 2.5,
      border: `1px solid ${theme.palette.divider}`,
    })}
  >
    <Stack direction="row" spacing={1.25} alignItems="center" mb={1.5}>
      <Box
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 32,
          height: 32,
          borderRadius: 1.5,
          color: 'primary.main',
          bgcolor: alpha(theme.palette.primary.main, 0.1),
        })}
      >
        {icon}
      </Box>
      <Typography variant="body2" fontWeight={700} color="text.secondary">
        {label}
      </Typography>
    </Stack>
    <Typography variant="h5" fontWeight={800}>
      {value}
    </Typography>
  </Box>
);

const BillingUsageHistory: React.FC = () => {
  // Shared with Billing AI / Billing Amount, so the three pages show the
  // same window (and therefore the same numbers) — see BillingTimeframeSelect.
  const [timeframe, setTimeframe] = useBillingTimeframe();
  const [metric, setMetric] = useState<MetricKey>('messagesUsed');

  const [daily, setDaily] = useState<UsageDailyResponse | null>(null);
  const [dailyLoading, setDailyLoading] = useState(true);
  const [dailyError, setDailyError] = useState<string | null>(null);

  const [periods, setPeriods] = useState<UsagePeriodRecord[]>([]);
  const [periodsTotal, setPeriodsTotal] = useState(0);
  const [periodsPage, setPeriodsPage] = useState(0);
  const [periodsLoading, setPeriodsLoading] = useState(true);
  const PERIODS_LIMIT = 12;

  useEffect(() => {
    const fetchDaily = async () => {
      try {
        setDailyLoading(true);
        setDailyError(null);
        // Custom ranges are LOCAL calendar days (never toISOString(), which
        // shifts to the previous UTC day behind UTC+0); billing periods are
        // UTC days. timeframeParams handles both; no params = current period.
        const result = await adminApi.getUsageDaily(timeframeParams(timeframe));
        setDaily(result);
      } catch (err: any) {
        setDailyError(err?.message || 'Failed to load usage history');
      } finally {
        setDailyLoading(false);
      }
    };
    fetchDaily();
  }, [timeframe]);

  useEffect(() => {
    const fetchPeriods = async () => {
      try {
        setPeriodsLoading(true);
        const result = await adminApi.getUsagePeriods({ page: periodsPage + 1, limit: PERIODS_LIMIT });
        setPeriods(result.data);
        setPeriodsTotal(result.meta.total);
      } catch {
        // Non-fatal — the daily trend above is the primary view; leave the table empty on error.
      } finally {
        setPeriodsLoading(false);
      }
    };
    fetchPeriods();
  }, [periodsPage]);

  const chartData = useMemo(
    () => (daily?.series || []).map((point) => ({ ...point, dateLabel: fmtDate(point.date) })),
    [daily]
  );

  const summary = daily?.summary;

  return (
    <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography variant="h4" fontWeight={800}>
            Usage History
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Daily trend for any date range, plus your archived monthly usage.
          </Typography>
        </Box>
        <BillingTimeframeSelect
          value={timeframe}
          onChange={setTimeframe}
          currentPeriod={daily?.currentPeriod}
          loadPeriods={async () => (await adminApi.getUsagePeriods({ page: 1, limit: 12 })).data}
        />
      </Stack>

      {dailyError && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {dailyError}
        </Alert>
      )}

      <Card variant="outlined" sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          {dailyLoading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Grid container spacing={2} mb={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <StatTile
                    icon={<Forum fontSize="small" />}
                    label="Messages"
                    value={METRIC_CONFIG.messagesUsed.format(summary?.messagesUsed || 0)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatTile
                    icon={<Api fontSize="small" />}
                    label="API Calls"
                    value={METRIC_CONFIG.apiCallsUsed.format(summary?.apiCallsUsed || 0)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatTile
                    icon={<SmartToy fontSize="small" />}
                    label="AI Tokens"
                    value={METRIC_CONFIG.aiTokensUsed.format(summary?.aiTokensUsed || 0)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatTile
                    icon={<MonetizationOn fontSize="small" />}
                    label="AI Cost"
                    value={METRIC_CONFIG.aiCostUsd.format(summary?.aiCostUsd || 0)}
                  />
                </Grid>
              </Grid>

              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
                <Typography variant="h6" fontWeight={800}>
                  Daily trend
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={metric}
                  onChange={(_, value) => {
                    if (value) setMetric(value);
                  }}
                  sx={{
                    bgcolor: 'action.hover',
                    borderRadius: 2,
                    p: 0.5,
                    '& .MuiToggleButton-root': {
                      border: 0,
                      borderRadius: '8px !important',
                      px: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      '&.Mui-selected': { bgcolor: 'background.paper', boxShadow: 1, '&:hover': { bgcolor: 'background.paper' } },
                    },
                  }}
                >
                  {(Object.keys(METRIC_CONFIG) as MetricKey[]).map((key) => (
                    <ToggleButton key={key} value={key}>
                      {METRIC_CONFIG[key].label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Stack>

              {chartData.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
                  <Typography color="text.secondary">No usage recorded in this range.</Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <defs>
                      <linearGradient id="usageHistoryFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#2563EB" stopOpacity={0.35} />
                        <stop offset="95%" stopColor="#2563EB" stopOpacity={0.03} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="dateLabel" tick={{ fontSize: 12 }} minTickGap={24} />
                    <YAxis
                      tick={{ fontSize: 12 }}
                      width={50}
                      {...(metric === 'aiTokensUsed' ? { tickFormatter: (v: number) => formatTokensCompact(v) } : {})}
                    />
                    <RechartsTooltip
                      formatter={(value: number) => METRIC_CONFIG[metric].format(value)}
                      labelFormatter={(label) => label}
                    />
                    <Area
                      type="monotone"
                      dataKey={metric}
                      name={METRIC_CONFIG[metric].label}
                      stroke="#2563EB"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#usageHistoryFill)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}

              {summary && summary.modelBreakdown.length > 0 && (
                <Box mt={3}>
                  <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
                    Model usage share
                  </Typography>
                  <Stack spacing={1}>
                    {summary.modelBreakdown.map((m: { model: string; tokens: number; cost: number }) => (
                      <Stack key={m.model} direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2">{m.model}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatTokensCompact(m.tokens)} tokens · ${m.cost.toFixed(2)}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Typography variant="h6" fontWeight={800} mb={0.5}>
            Monthly archive
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2.5}>
            One record per completed billing cycle — permanent, never edited after creation.
          </Typography>

          {periodsLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={28} />
            </Box>
          ) : periods.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No completed billing cycles yet — your current period is still in progress.
            </Typography>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Period</TableCell>
                      <TableCell>Plan</TableCell>
                      <TableCell align="right">Messages</TableCell>
                      <TableCell align="right">API Calls</TableCell>
                      <TableCell align="right">AI Tokens</TableCell>
                      <TableCell align="right">AI Cost</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {periods.map((p) => (
                      <TableRow key={p._id}>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2">
                              {fmtDateFull(p.periodStart)} – {fmtDateFull(p.periodEnd)}
                            </Typography>
                            {p.isLegacySnapshot && (
                              <Chip
                                size="small"
                                icon={<History sx={{ fontSize: 14 }} />}
                                label="Legacy"
                                sx={{ height: 20, fontSize: 11, fontWeight: 700 }}
                              />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ textTransform: 'capitalize' }}>{p.planTypeAtClose}</TableCell>
                        <TableCell align="right">
                          {p.messagesUsed.toLocaleString()} / {p.messagesLimitAtClose > 0 ? p.messagesLimitAtClose.toLocaleString() : '∞'}
                        </TableCell>
                        <TableCell align="right">
                          {p.apiCallsUsed.toLocaleString()} / {p.apiCallsLimitAtClose > 0 ? p.apiCallsLimitAtClose.toLocaleString() : '∞'}
                        </TableCell>
                        <TableCell align="right">
                          {p.isLegacySnapshot ? '—' : formatTokensCompact(p.aiTokensUsed)}
                        </TableCell>
                        <TableCell align="right">
                          {p.isLegacySnapshot ? '—' : `$${p.aiCostUsd.toFixed(2)}`}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={periodsTotal}
                page={periodsPage}
                onPageChange={(_e, p) => setPeriodsPage(p)}
                rowsPerPage={PERIODS_LIMIT}
                rowsPerPageOptions={[PERIODS_LIMIT]}
              />
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default BillingUsageHistory;
